import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { calculateReduceAllocation, ReduceAllocationCalculateInput } from '../../composables/fetchAPI';
import { fetchCryptoPrice, getStoredCryptoDetails, cryptoPairLabel } from '../../composables/fetchCrypto';
import { formatCryptoQty, formatCryptoUsd, sanitizeDecimalInput, validateCryptoDecimal, num, shortenDecimalText } from '../../utils/cryptoDecimal';

export interface ReduceAllocationHolding {
  portId: string;
  portName: string;
  subPortId: string;
  subPortName: string;
  symbol: string;
  quantity: number;
  avgCost: number;
  currentPrice: number;
  weightPct: number | null;
  // สัดส่วนเป้าหมายของหุ้นนี้ในพอร์ตย่อย (expectedRatio) ใช้ตั้งค่า default ให้ช่อง "ต้องการให้สัดส่วนเหลือ"
  expectedRatio?: number;
  // พอร์ตคริปโต: symbol เป็น X:BTCUSD และส่งราคาตรงตัวผ่าน price.decimalValue
  isCrypto?: boolean;
}

interface ReduceAllocationToolProps {
  holding: ReduceAllocationHolding | null;
  portfolioVersion: 'V1' | 'V2';
  placeholderMessage?: string;
  // เงินสดปัจจุบันของพอร์ตใหญ่ที่ถือ holding นี้อยู่ (portfolio.summary.cashValue / cashBalances[0].amount)
  // ใช้ประมาณ "เงินสดหลังขาย" เท่านั้น ไม่ใช่ค่าที่ backend คำนวณคืนมา (endpoint นี้ไม่รวมเงินสดในผลลัพธ์)
  currentCash?: number;
}

// โครงสร้างจริงจาก POST /tools/portfolio/reduce-allocation/calculate
// หมายเหตุ: เคส sell.required=false (สัดส่วนไม่เกินเป้าอยู่แล้ว) backend อาจไม่ส่ง holding/projected
// กลับมาครบ จึงทำทุก field ยกเว้น sell/pricing ให้ optional และ fallback ในฝั่ง UI แทน
interface ReduceAllocationResult {
  holding?: { symbol: string; quantity: number; currentMarketValue: number; currentRatio: number };
  target?: { ratio: number; marketValue: number };
  sell?: {
    required: boolean;
    quantity: number;
    grossValue: number;
    percentageOfHolding: number;
    isFullExit: boolean;
  };
  projected?: {
    quantity: number;
    marketValue: number;
    ratio: number;
    differenceFromTarget: number;
    portfolioMarketValue: number;
  };
  pricing?: { mode: string; usedPrice: number; currency: string; stale?: boolean };
  executionContext?: { costBasisMethod?: string };
  warnings?: string[];
  // ต้องส่ง options.includeCostBasisProjection:true ไปตอน request ถึงจะมี field นี้กลับมา
  costBasisProjection?: {
    method?: string;
    current?: { quantity: number; costAmount: number; averageCost: number };
    sold?: { quantity: number; costAmount: number };
    remaining?: { quantity: number; costAmount: number; averageCost: number };
    realizedGain?: number;
  };
}

// พอร์ตคริปโต backend ส่งสัดส่วนเป็น string ทศนิยมยาว (เช่น "90.0000…0182") — String.toLocaleString ไม่จัดรูปให้
// จึงต้องแปลงเป็นตัวเลขก่อนเสมอ (แสดงผลเท่านั้น)
const pct = (n: number | string) => `${num(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

// ชื่อและคำอธิบายวิธีคิดต้นทุนขาย (costBasisMethod) สำหรับแสดงให้ผู้ใช้อ่านเข้าใจ
const COST_BASIS_METHODS: Record<string, { label: string; description: string }> = {
  COMPRESSED_FIFO: {
    label: 'ซื้อก่อน ขายก่อน แบบรวมล็อต',
    description: 'รวมรายการซื้อตามกติกาของพอร์ต แล้วขายจากล็อตที่เก่าที่สุดก่อน',
  },
  FIFO: { label: 'ซื้อก่อน ขายก่อน', description: 'ขายหุ้นจากรายการซื้อที่เก่าที่สุดก่อน' },
  WAC: { label: 'ต้นทุนเฉลี่ยถ่วงน้ำหนัก', description: 'ใช้ต้นทุนเฉลี่ยของหุ้นที่ถืออยู่คำนวณต้นทุนขาย' },
  TAX_MINIMIZATION: {
    label: 'เลือกล็อตเพื่อลดภาษี',
    description: 'เลือกล็อตขายตามกติกาที่มุ่งลดภาษี ผลจริงขึ้นอยู่กับเงื่อนไขภาษีที่ใช้',
  },
};

// หัวข้อส่วนต้นทุนเฉลี่ย พร้อมบอกวิธีคิดต้นทุนเป็นภาษาไทย + คำอธิบาย (ไม่รู้จักวิธีไหนก็แสดงรหัสเดิม)
const CostBasisHeading: React.FC<{ title: string; method?: string }> = ({ title, method }) => {
  const info = method ? COST_BASIS_METHODS[method] : undefined;
  return (
    <div className="text-sm" style={{ color: 'var(--text-color)' }}>
      <span className="block">
        {title}
        {method ? ` · คิดแบบ ${info?.label ?? method}` : ''}
      </span>
      {info && (
        <span className="block text-xs opacity-80">
          {info.description} ({method})
        </span>
      )}
    </div>
  );
};

const ArrowRight = () => (
  <svg className="flex-shrink-0" width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 12h14.5M13 5.5l6.5 6.5-6.5 6.5"
      stroke="var(--yellow-text-color)"
      strokeWidth="2.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// การ์ด "ก่อน → หลัง" ใช้ร่วมกันทั้งสัดส่วนและต้นทุนเฉลี่ย
const BeforeAfterCard: React.FC<{ beforeLabel: string; before: string; afterLabel: string; after: string }> = ({
  beforeLabel,
  before,
  afterLabel,
  after,
}) => (
  <div
    className="flex items-center justify-between gap-3 rounded-lg p-4"
    style={{ background: 'var(--bg-item)', boxShadow: 'var(--box-shadow)' }}
  >
    <div>
      <span className="block text-xs" style={{ color: 'var(--text-color)' }}>{beforeLabel}</span>
      <strong className="text-2xl">{before}</strong>
    </div>
    <ArrowRight />
    <div className="text-right">
      <span className="block text-xs" style={{ color: 'var(--text-color)' }}>{afterLabel}</span>
      <strong className="text-2xl" style={{ color: 'var(--green-text-color)' }}>{after}</strong>
    </div>
  </div>
);

const ReduceAllocationTool: React.FC<ReduceAllocationToolProps> = ({ holding, portfolioVersion, placeholderMessage, currentCash }) => {
  const isCrypto = !!holding?.isCrypto;
  const unit = isCrypto ? 'เหรียญ' : 'หุ้น';
  // คริปโต: ยอดเล็กมากไม่ปัดเป็น 0.00 / จำนวนเหรียญแสดงตรงตัว
  const money = (n: number) => {
    const v = num(n);
    return (v < 0 ? '-$' : '$') + (isCrypto ? formatCryptoUsd(Math.abs(v)) : Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  };
  // จำนวนเหรียญจาก backend ยาวได้ถึง 36 ตำแหน่ง → ตัดเหลือ 8 หลักทศนิยมแล้วต่อ "…" (แสดงผลเท่านั้น)
  const qtyText = (q: any) => (isCrypto ? shortenDecimalText(formatCryptoQty(q), 8) : num(q).toLocaleString(undefined, { maximumFractionDigits: 4 }));
  const [targetRatio, setTargetRatio] = useState('');
  const [priceMode, setPriceMode] = useState<'MARKET' | 'MANUAL'>('MARKET');
  const [manualPrice, setManualPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ReduceAllocationResult | null>(null);

  useEffect(() => {
    setResult(null);
    setManualPrice(holding?.currentPrice ? String(holding.currentPrice) : '');
    if (holding?.isCrypto && holding.symbol) {
      const exact = getStoredCryptoDetails(holding.symbol).priceDecimal;
      if (exact) setManualPrice(exact);
    }
    // Default ช่องสัดส่วนเป้าหมายเป็น "เป้า" ของหุ้นนี้ (expectedRatio) เฉพาะกรณีที่เป้าน้อยกว่าหรือเท่ากับ
    // สัดส่วนที่ถืออยู่ตอนนี้ (คือมีของขายได้จริง) ถ้าเป้าสูงกว่าที่ถืออยู่ (ต้องซื้อเพิ่ม ไม่ใช่ขาย) หรือเป้าเป็น 0
    // (ยังไม่ได้ตั้งเป้าไว้จริงๆ) ให้เว้นว่างไว้เหมือนเดิม ให้ผู้ใช้กรอกเอง
    if (
      holding?.expectedRatio !== undefined &&
      holding.expectedRatio > 0 &&
      holding.weightPct !== null &&
      holding.expectedRatio <= holding.weightPct
    ) {
      setTargetRatio(String(holding.expectedRatio));
    } else {
      setTargetRatio('');
    }
  }, [holding?.symbol, holding?.portId, holding?.subPortId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!holding) return;

    const ratio = Number(targetRatio);
    if (!targetRatio.trim() || Number.isNaN(ratio) || ratio < 0) {
      toast.error('กรุณากรอกสัดส่วนเป้าหมายให้ถูกต้อง');
      return;
    }
    if (priceMode === 'MANUAL' && (!manualPrice.trim() || Number.isNaN(Number(manualPrice)))) {
      toast.error('กรุณากรอกราคาที่ต้องการใช้คำนวณ');
      return;
    }

    // คริปโต: ราคาตลาดฝั่งหลังบ้านยังดึงไม่ได้ (MARKET_PRICE_UNAVAILABLE)
    // จึงใช้ราคาล่าสุดจาก gateway ฝั่งหน้าเว็บ แล้วส่งแบบ MANUAL ผ่าน decimalValue (ตรงตัว ไม่ผ่าน Number)
    let cryptoPrice: string | null = null;
    if (isCrypto) {
      if (priceMode === 'MANUAL') {
        const err = validateCryptoDecimal(manualPrice);
        if (err) {
          toast.error(`ราคา: ${err}`);
          return;
        }
        cryptoPrice = manualPrice.trim();
      } else {
        await fetchCryptoPrice(holding.symbol);
        cryptoPrice = getStoredCryptoDetails(holding.symbol).priceDecimal;
        if (!cryptoPrice) {
          toast.error('ยังไม่มีราคาตลาดของเหรียญนี้ กรุณาระบุราคาเอง');
          setPriceMode('MANUAL');
          return;
        }
      }
    }

    const payload: ReduceAllocationCalculateInput = {
      portfolioVersion,
      portfolioId: holding.portId,
      subPortId: holding.subPortId,
      symbol: holding.symbol,
      targetRatio: ratio,
      price: cryptoPrice
        ? { mode: 'MANUAL', value: num(cryptoPrice), decimalValue: cryptoPrice }
        : priceMode === 'MANUAL' ? { mode: 'MANUAL', value: Number(manualPrice) } : { mode: 'MARKET' },
      // ขอ cost basis projection เฉพาะ V2 เท่านั้น พอร์ต V1 ไม่รองรับ option นี้
      ...(portfolioVersion === 'V2' ? { options: { includeCostBasisProjection: true } } : {}),
    };

    setLoading(true);
    try {
      const res = await calculateReduceAllocation(payload);
      setResult(res);
    } catch (error: any) {
      toast.error(error?.th_message || error?.message || 'คำนวณไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!holding ? (
        <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--text-color)' }}>
          {placeholderMessage || 'เลือกหุ้นหรือเหรียญจากรายการด้านซ้ายเพื่อเริ่มคำนวณ'}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <span className="font-bold text-base" style={{ color: 'var(--yellow-text-color)' }}>{cryptoPairLabel(holding.symbol)}</span>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-item)', color: 'var(--text-color)' }}>
              จำลองเท่านั้น
            </span>
          </div>
          <div className="text-sm" style={{ color: 'var(--text-color)' }}>
            {holding.portName} · {holding.subPortName} · {qtyText(holding.quantity)} {unit}
          </div>

          {holding.weightPct !== null && (
            <div className="flex items-center justify-between gap-3 rounded-lg p-3" style={{ background: 'var(--bg-item)' }}>
              <div>
                <span className="block text-xs" style={{ color: 'var(--text-color)' }}>สัดส่วนปัจจุบัน</span>
                <strong className="text-base">{pct(holding.weightPct)}</strong>
              </div>
              <div className="text-right">
                <span className="block text-xs" style={{ color: 'var(--text-color)' }}>มูลค่าที่ถือ</span>
                <strong className="text-base">{money(holding.quantity * holding.currentPrice)}</strong>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm" style={{ color: 'var(--text-color)' }}>
              ต้องการให้สัดส่วนเหลือ
            </label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                value={targetRatio}
                onChange={(e) => setTargetRatio(e.target.value)}
                className="flex-1 rounded-lg px-3 py-2 text-2xl outline-none"
                style={{ background: 'var(--bg-item)', color: 'var(--text-color)', border: '1px solid var(--text-input)' }}
                placeholder="เช่น 10"
              />
              <span className="text-sm whitespace-nowrap" style={{ color: 'var(--text-color)' }}>% ของพอร์ต</span>
            </div>
            <div className="mt-1 text-xs" style={{ color: 'var(--text-color)' }}>
              ใช้สัดส่วนของพอร์ตที่เลือก · คำนวณให้อัตโนมัติ
            </div>
          </div>

          <div className="flex gap-2 text-sm">
            <button
              type="button"
              onClick={() => setPriceMode('MARKET')}
              className="flex-1 rounded-lg py-2 font-semibold leading-tight"
              style={
                priceMode === 'MARKET'
                  ? { background: 'var(--tab-selected)', color: '#fff' }
                  : { background: 'var(--bg-item)', color: 'var(--text-color)' }
              }
            >
              <span className="block">ใช้ราคาตลาด</span>
              <span className="block text-xs font-normal opacity-80">{money(holding.currentPrice)}</span>
            </button>
            <button
              type="button"
              onClick={() => setPriceMode('MANUAL')}
              className="flex-1 rounded-lg py-2 font-semibold"
              style={
                priceMode === 'MANUAL'
                  ? { background: 'var(--tab-selected)', color: '#fff' }
                  : { background: 'var(--bg-item)', color: 'var(--text-color)' }
              }
            >
              กำหนดราคาเอง
            </button>
          </div>

          {priceMode === 'MANUAL' && (
            <input
              {...(isCrypto
                ? { type: 'text', inputMode: 'decimal' as const, autoComplete: 'off' }
                : { type: 'number', min: '0', step: '0.0001' })}
              value={manualPrice}
              onChange={(e) => setManualPrice(isCrypto ? sanitizeDecimalInput(e.target.value) : e.target.value)}
              className="w-full rounded-lg px-3 py-2 text-sm outline-none"
              style={{ background: 'var(--bg-item)', color: 'var(--text-color)', border: '1px solid var(--text-input)' }}
              placeholder={`ราคาต่อ${unit}ที่ใช้คำนวณ`}
            />
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg py-2 font-bold text-sm disabled:opacity-50"
            style={{ background: 'var(--button-create-bg)', color: 'var(--button-text-color)' }}
          >
            {loading ? 'กำลังคำนวณ...' : 'คำนวณ'}
          </button>

          {result && (() => {
            // เดินผ่าน optional chaining ทุกจุด เพราะเคสที่ไม่ต้องขาย (sell.required=false) backend
            // อาจไม่ส่ง holding/projected กลับมาเต็มรูปแบบ จึง fallback ไปใช้ค่าที่รู้อยู่แล้วจากฝั่ง client แทน
            const sellRequired = !!result.sell?.required;
            const sellGross = result.sell?.grossValue ?? 0;
            const sellQty = result.sell?.quantity ?? 0;
            const usedPrice = result.pricing?.usedPrice ?? holding.currentPrice;
            // ใช้สัดส่วนที่คำนวณฝั่ง frontend เป็นหลัก (อ้างอิงราคาตลาดปัจจุบันจริง) เพราะ backend อาจคำนวณจาก
            // ราคาที่ค้างอยู่ (เช่นตอน pricing.stale=true) ทำให้ currentRatio ไม่ตรงกับที่แสดงใน "สัดส่วนปัจจุบัน" ด้านบน
            const beforeRatio = holding.weightPct ?? result.holding?.currentRatio ?? 0;
            const parsedTarget = Number(targetRatio);
            const afterRatio = result.projected?.ratio ?? (Number.isFinite(parsedTarget) ? parsedTarget : beforeRatio);
            const remainingValue = result.projected?.marketValue ?? holding.quantity * holding.currentPrice;
            const cashAfter = num(currentCash) + (sellRequired ? num(sellGross) : 0);

            return (
              <div className="mt-2 pt-3 space-y-3" style={{ borderTop: '1px solid var(--bg-item)' }}>
                <div>
                  <span className="text-sm" style={{ color: 'var(--text-color)' }}>
                    มูลค่า{unit}ที่ต้องขายโดยประมาณ
                  </span>
                  <div className="text-3xl font-bold" style={{ color: 'var(--yellow-text-color)' }}>
                    {money(sellRequired ? sellGross : 0)}
                  </div>
                  <div className="text-sm mt-0.5" style={{ color: 'var(--text-color)' }}>
                    {sellRequired ? (
                      <>
                        ประมาณ{' '}
                        <span className="font-bold" style={{ color: 'var(--yellow-text-color)' }}>
                          {qtyText(sellQty)} {unit}
                        </span>{' '}
                        · ราคาอ้างอิง{' '}
                        <span className="font-bold" style={{ color: 'var(--green-text-color)' }}>
                          {money(usedPrice)}
                        </span>
                      </>
                    ) : (
                      'สัดส่วนปัจจุบันไม่เกินเป้าหมายแล้ว ไม่จำเป็นต้องขาย'
                    )}
                  </div>
                </div>

                <BeforeAfterCard beforeLabel="ก่อนขาย" before={pct(beforeRatio)} afterLabel="หลังขาย" after={pct(afterRatio)} />

                <div className="flex justify-between gap-3 text-sm">
                  <span style={{ color: 'var(--text-color)' }}>มูลค่า{unit}ที่เหลือ</span>
                  <strong>{money(remainingValue)}</strong>
                </div>
                <div className="flex justify-between gap-3 text-sm">
                  <span style={{ color: 'var(--text-color)' }}>เงินสดหลังขาย</span>
                  <strong>{money(cashAfter)}</strong>
                </div>

                {(() => {
                  // ทางเลือกที่ 1: ถ้า backend ส่ง costBasisProjection กลับมาจริง (เพราะเราขอผ่าน
                  // options.includeCostBasisProjection) ให้ใช้ remaining.averageCost ตรงๆ เพราะเป็นตัวเลขที่
                  // คำนวณถูกต้องสำหรับทุกวิธีคิดต้นทุน (WAC, FIFO, TAX_MINIMIZATION ฯลฯ) ไม่ต้องเดาเองฝั่ง client
                  const proj = result.costBasisProjection;
                  const avgCostAfter = proj?.remaining?.averageCost;
                  const hasProjection = typeof avgCostAfter === 'number' && Number.isFinite(avgCostAfter);

                  if (hasProjection) {
                    const avgCostBefore = proj?.current?.averageCost ?? holding.avgCost;
                    const realizedGain = proj?.realizedGain;
                    return (
                      <div className="pt-3 space-y-2" style={{ borderTop: '1px solid var(--bg-item)' }}>
                        <CostBasisHeading title="ต้นทุนเฉลี่ยหลังขาย (ประมาณ)" method={proj?.method} />
                        <BeforeAfterCard
                          beforeLabel="ก่อนขาย (ต้นทุนเฉลี่ย)"
                          before={money(avgCostBefore)}
                          afterLabel="หลังขาย (ต้นทุนเฉลี่ย)"
                          after={money(avgCostAfter as number)}
                        />
                        {typeof realizedGain === 'number' && (
                          <div className="pt-2 text-right">
                            <span className="block text-sm" style={{ color: 'var(--text-color)' }}>
                              กำไร/ขาดทุนที่รับรู้โดยประมาณ
                            </span>
                            <strong
                              className="text-3xl"
                              style={{ color: realizedGain >= 0 ? 'var(--green-text-color)' : 'var(--red-text-color)' }}
                            >
                              {realizedGain >= 0 ? '+' : '-'}{money(Math.abs(realizedGain))}
                            </strong>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // ทางเลือกที่ 2: ไม่มี projection จริงจาก backend (เช่น เวอร์ชัน backend ยังไม่รองรับ option นี้)
                  // "ไม่เปลี่ยน" เป็นจริงเฉพาะวิธี WAC เท่านั้น วิธีอื่น (เลือกล็อตเฉพาะ) คำนวณแม่นยำจากฝั่งนี้ไม่ได้
                  const costBasisMethod = result.executionContext?.costBasisMethod;
                  const isWac = costBasisMethod === 'WAC';
                  return (
                    <div className="pt-3 space-y-2" style={{ borderTop: '1px solid var(--bg-item)' }}>
                      <CostBasisHeading
                        title={isWac ? 'ต้นทุนเฉลี่ยหลังขาย (ประมาณ)' : 'ต้นทุนเฉลี่ยปัจจุบัน (ก่อนขาย)'}
                        method={costBasisMethod}
                      />
                      {isWac ? (
                        <BeforeAfterCard
                          beforeLabel="ก่อนขาย (ต้นทุนเฉลี่ย)"
                          before={money(holding.avgCost)}
                          afterLabel="หลังขาย (ต้นทุนเฉลี่ย)"
                          after={money(holding.avgCost)}
                        />
                      ) : (
                        <strong className="block text-xl">{money(holding.avgCost)} / {unit}</strong>
                      )}
                      {!isWac && (
                        <span className="block text-sm font-bold" style={{ color: 'var(--yellow-text-color)' }}>
                          พอร์ตนี้ใช้วิธี{costBasisMethod ? `${COST_BASIS_METHODS[costBasisMethod]?.label ?? costBasisMethod}` : 'เลือกล็อตเฉพาะ'} ต้นทุนเฉลี่ยของหุ้นที่เหลือหลังขายจริงอาจเปลี่ยนไปจากนี้ และเครื่องมือนี้ยังคำนวณให้ไม่ได้
                        </span>
                      )}
                    </div>
                  );
                })()}

                {result.pricing?.stale && (
                  <div className="text-sm font-bold" style={{ color: 'var(--red-text-color)' }}>
                    ราคาที่ใช้คำนวณอาจไม่ใช่ราคาล่าสุด กรุณาตรวจสอบก่อนตัดสินใจ
                  </div>
                )}

                <div
                  className="flex items-start gap-2.5 px-4 py-3 rounded-lg text-sm font-bold"
                  style={{ background: 'var(--theme-dark-red-bg)', border: '1px solid var(--theme-dark-red-border)', color: 'var(--red-text-color)' }}
                >
                  <span className="text-xl leading-none flex-shrink-0" style={{ color: 'var(--yellow-text-color)' }} aria-hidden="true">
                    ⚠
                  </span>
                  <span>
                    ประมาณจากราคาล่าสุดที่ระบบได้รับ ราคาซื้อขายจริงอาจต่างไป ส่วนต้นทุนเฉลี่ยของหุ้นที่เหลือและกำไร/ขาดทุนจากการขาย
                    คิดตามวิธีคิดต้นทุนของพอร์ต
                  </span>
                </div>
              </div>
            );
          })()}
        </form>
      )}
    </>
  );
};

export default ReduceAllocationTool;
